import { useEffect, useState } from "react";
import Swal from "sweetalert2";
import { apiRequest } from "../../core/common/api";
import ToggleSwitch from "../../shared/ToggleSwitch";

type SettingsForm = {
  general: {
    appName: string;
    tagline: string;
    supportEmail: string;
    supportPhone: string;
    whatsappNumber: string;
    address: string;
  };
  features: {
    maintenanceMode: boolean;
    allowNewRegistrations: boolean;
    allowReviews: boolean;
  };
  orders: {
    defaultShippingCharge: string;
    returnWindowDays: string;
    allowCod: boolean;
    allowOnline: boolean;
    allowCredit: boolean;
    creditReminderDays: string;
  };
};

const emptyForm = (): SettingsForm => ({
  general: {
    appName: "Saree House",
    tagline: "",
    supportEmail: "",
    supportPhone: "",
    whatsappNumber: "",
    address: "",
  },
  features: {
    maintenanceMode: false,
    allowNewRegistrations: true,
    allowReviews: true,
  },
  orders: {
    defaultShippingCharge: "0",
    returnWindowDays: "7",
    allowCod: true,
    allowOnline: true,
    allowCredit: true,
    creditReminderDays: "30",
  },
});

const mapSettingsToForm = (settings: any): SettingsForm => ({
  general: {
    appName: settings?.general?.appName || "Saree House",
    tagline: settings?.general?.tagline || "",
    supportEmail: settings?.general?.supportEmail || "",
    supportPhone: settings?.general?.supportPhone || "",
    whatsappNumber: settings?.general?.whatsappNumber || "",
    address: settings?.general?.address || "",
  },
  features: {
    maintenanceMode: settings?.features?.maintenanceMode === true,
    allowNewRegistrations: settings?.features?.allowNewRegistrations !== false,
    allowReviews: settings?.features?.allowReviews !== false,
  },
  orders: {
    defaultShippingCharge: String(settings?.orders?.defaultShippingCharge ?? 0),
    returnWindowDays: String(settings?.orders?.returnWindowDays ?? 7),
    allowCod: settings?.orders?.allowCod !== false,
    allowOnline: settings?.orders?.allowOnline !== false,
    allowCredit: settings?.orders?.allowCredit !== false,
    creditReminderDays: String(settings?.orders?.creditReminderDays ?? 30),
  },
});

const Settings = () => {
  const [form, setForm] = useState<SettingsForm>(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const result = await apiRequest("/settings", "GET");
      setForm(mapSettingsToForm(result.settings));
    } catch (error: any) {
      Swal.fire("Error", error.message || "Unable to load settings", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadSettings();
  }, []);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!form.general.appName.trim()) {
      Swal.fire("Validation Error", "App name is required", "error");
      return;
    }

    if (!form.orders.allowCod && !form.orders.allowOnline && !form.orders.allowCredit) {
      Swal.fire("Validation Error", "Enable at least one payment method", "error");
      return;
    }

    const reminderDays = Number(form.orders.creditReminderDays);
    if (!Number.isInteger(reminderDays) || reminderDays < 0 || reminderDays > 365) {
      Swal.fire(
        "Validation Error",
        "Credit reminder days must be a whole number between 0 and 365",
        "error",
      );
      return;
    }

    setSaving(true);
    try {
      const result = await apiRequest("/settings", "PUT", {
        general: {
          ...form.general,
          appName: form.general.appName.trim(),
        },
        features: form.features,
        orders: {
          defaultShippingCharge: Number(form.orders.defaultShippingCharge || 0),
          returnWindowDays: Number(form.orders.returnWindowDays || 0),
          allowCod: form.orders.allowCod,
          allowOnline: form.orders.allowOnline,
          allowCredit: form.orders.allowCredit,
          creditReminderDays: reminderDays,
        },
      });

      if (!result.success) {
        throw new Error(result.message || "Unable to save settings");
      }

      setForm(mapSettingsToForm(result.settings));
      Swal.fire({
        title: "Settings Saved",
        text: "App settings have been updated.",
        icon: "success",
        timer: 1400,
        showConfirmButton: false,
      });
    } catch (error: any) {
      Swal.fire("Save Failed", error.message || "Unable to save settings", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="page-section loading-state">
        <h2>Loading Settings…</h2>
      </div>
    );
  }

  return (
    <div className="detail-page">
      <div className="page-hdr">
        <div className="page-hdr-left">
          <div className="page-eyebrow">Configuration</div>
          <h1 className="page-title">
            App <em>Settings</em>
          </h1>
          <div className="page-sub">
            Manage store details, checkout rules and app features.
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="page-stack">
        <section className="table-card">
          <div className="table-head">
            <h2>General</h2>
            <p>Brand and support details shown in the app</p>
          </div>
          <div className="admin-card-body admin-form-grid">
            <div className="form-field">
              <label className="modal-label">App Name</label>
              <input
                className="admin-input"
                value={form.general.appName}
                onChange={(event) =>
                  setForm({
                    ...form,
                    general: { ...form.general, appName: event.target.value },
                  })
                }
                placeholder="Saree House"
                required
              />
            </div>
            <div className="form-field">
              <label className="modal-label">Tagline</label>
              <input
                className="admin-input"
                value={form.general.tagline}
                onChange={(event) =>
                  setForm({
                    ...form,
                    general: { ...form.general, tagline: event.target.value },
                  })
                }
                placeholder="Ethnic wear for every occasion"
              />
            </div>
            <div className="form-field">
              <label className="modal-label">Support Email</label>
              <input
                type="email"
                className="admin-input"
                value={form.general.supportEmail}
                onChange={(event) =>
                  setForm({
                    ...form,
                    general: { ...form.general, supportEmail: event.target.value },
                  })
                }
                placeholder="support@sareehouse.com"
              />
            </div>
            <div className="form-field">
              <label className="modal-label">Support Phone</label>
              <input
                className="admin-input"
                value={form.general.supportPhone}
                onChange={(event) =>
                  setForm({
                    ...form,
                    general: { ...form.general, supportPhone: event.target.value },
                  })
                }
                placeholder="9876543210"
              />
            </div>
            <div className="form-field">
              <label className="modal-label">WhatsApp Number</label>
              <input
                className="admin-input"
                value={form.general.whatsappNumber}
                onChange={(event) =>
                  setForm({
                    ...form,
                    general: { ...form.general, whatsappNumber: event.target.value },
                  })
                }
                placeholder="9876543210"
              />
            </div>
            <div className="form-field">
              <label className="modal-label">Store Address</label>
              <input
                className="admin-input"
                value={form.general.address}
                onChange={(event) =>
                  setForm({
                    ...form,
                    general: { ...form.general, address: event.target.value },
                  })
                }
                placeholder="Jaipur, Rajasthan"
              />
            </div>
          </div>
        </section>

        <section className="table-card">
          <div className="table-head">
            <h2>App Features</h2>
            <p>Turn key app capabilities on or off</p>
          </div>
          <div className="admin-card-body page-stack">
            <ToggleSwitch
              label="Maintenance Mode"
              hint="App can show a maintenance screen using this flag"
              checked={form.features.maintenanceMode}
              onChange={(checked) =>
                setForm({
                  ...form,
                  features: { ...form.features, maintenanceMode: checked },
                })
              }
            />
            <ToggleSwitch
              label="Allow New Registrations"
              hint="Disable to stop OTP register for new customers"
              checked={form.features.allowNewRegistrations}
              onChange={(checked) =>
                setForm({
                  ...form,
                  features: { ...form.features, allowNewRegistrations: checked },
                })
              }
            />
            <ToggleSwitch
              label="Allow Reviews"
              hint="Disable to block new and edited product reviews"
              checked={form.features.allowReviews}
              onChange={(checked) =>
                setForm({
                  ...form,
                  features: { ...form.features, allowReviews: checked },
                })
              }
            />
          </div>
        </section>

        <section className="table-card">
          <div className="table-head">
            <h2>Orders & Payments</h2>
            <p>Shipping, returns and enabled payment methods</p>
          </div>
          <div className="admin-card-body page-stack">
            <div className="admin-form-grid">
              <div className="form-field">
                <label className="modal-label">Default Shipping Charge (₹)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className="admin-input"
                  value={form.orders.defaultShippingCharge}
                  onChange={(event) =>
                    setForm({
                      ...form,
                      orders: { ...form.orders, defaultShippingCharge: event.target.value },
                    })
                  }
                />
                <small className="form-hint">Auto-fills in product add. Can be changed per product.</small>
              </div>
              <div className="form-field">
                <label className="modal-label">Return Window (Days)</label>
                <input
                  type="number"
                  min="0"
                  className="admin-input"
                  value={form.orders.returnWindowDays}
                  onChange={(event) =>
                    setForm({
                      ...form,
                      orders: { ...form.orders, returnWindowDays: event.target.value },
                    })
                  }
                />
              </div>
              <div className="form-field">
                <label className="modal-label">Credit Reminder (Days)</label>
                <input
                  type="number"
                  min="0"
                  max="365"
                  className="admin-input"
                  value={form.orders.creditReminderDays}
                  onChange={(event) =>
                    setForm({
                      ...form,
                      orders: { ...form.orders, creditReminderDays: event.target.value },
                    })
                  }
                />
                <small className="form-hint">
                  After this many days, unpaid credit users get a reminder every day. 0 turns reminders off.
                </small>
              </div>
            </div>
            <ToggleSwitch
              label="Cash on Delivery"
              checked={form.orders.allowCod}
              onChange={(checked) =>
                setForm({ ...form, orders: { ...form.orders, allowCod: checked } })
              }
            />
            <ToggleSwitch
              label="Online Payment"
              checked={form.orders.allowOnline}
              onChange={(checked) =>
                setForm({ ...form, orders: { ...form.orders, allowOnline: checked } })
              }
            />
            <ToggleSwitch
              label="Credit Payment"
              checked={form.orders.allowCredit}
              onChange={(checked) =>
                setForm({ ...form, orders: { ...form.orders, allowCredit: checked } })
              }
            />
          </div>
        </section>

        <div className="page-hdr-actions">
          <button type="submit" className="btn-sm btn-primary" disabled={saving}>
            {saving ? "Saving..." : "Save Settings"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default Settings;
